import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { RoleToTypeRule, RoleMatchType } from './types';
import {
  addRulesFromDraft,
  deleteRule,
  emptyDraft,
  isDraftValid,
  moveRule,
  updateRule,
  validateDraft,
  type RuleDraft,
} from './roleRulesReducer';
import { USER_TYPE_OPTIONS } from './userTypes';

/**
 * Role-mapping rule editor (SF-3180, Task 2).
 *
 * Manages the ordered `roleToType[]` array the admin will PATCH to
 * sf-auth-service. Each rule maps an IdP role claim value → a ServeFirst
 * `user.type`. Order matters: the backend resolves the FIRST matching rule
 * per sf-auth-service `src/utils/roleMapping.ts`, so the up/down controls
 * are semantic, not cosmetic.
 *
 * Scope for Task 2:
 *  - Create / edit / delete / reorder UI.
 *  - Inline validation that mirrors `validateRoleToType` in
 *    sf-auth-service so operators get immediate feedback before save.
 *  - Pure controlled component — save wiring lands in Task 3/4.
 *
 * Out of scope for Task 2:
 *  - Regex "safe" (ReDoS) heuristic — the backend enforces it; we surface
 *    the backend error message on save in Task 4 rather than duplicating
 *    the static analysis here.
 *  - `existingUserRolePolicy` / `defaultType` selectors (Task 3).
 */
export interface RoleMappingRulesEditorProps {
  value: RoleToTypeRule[];
  onChange: (rules: RoleToTypeRule[]) => void;
  /** When true, all controls are non-interactive (matches parent form's
   *  `disabled` prop so the Task 1 view-only mode still works). */
  disabled?: boolean;
}

interface RuleRow extends RoleToTypeRule {
  /**
   * Stable UI-only row key. Assigned on add, preserved across reorder,
   * dropped on delete. We deliberately don't persist this to the wire
   * format — the backend has no concept of rule ids (the model treats
   * the array as ordered-by-position).
   */
  key: number;
}

const MATCH_OPTIONS: { value: RoleMatchType; label: string }[] = [
  { value: 'exact', label: 'Exact match' },
  { value: 'regex', label: 'Regex' },
];

function userTypeLabel(type: string): string {
  return USER_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

export const RoleMappingRulesEditor: React.FC<RoleMappingRulesEditorProps> = ({
  value,
  onChange,
  disabled,
}) => {
  // `editing` is either null (no dialog), { index: null } for "new", or
  // { index: n } for "edit existing at n". Draft state lives here so we
  // never mutate the committed list until the operator clicks Save.
  const [editing, setEditing] = useState<{ index: number | null } | null>(null);
  const [draft, setDraft] = useState<RuleDraft>(emptyDraft());

  // Stable UI keys for each rule, parallel to `value`. We assign a fresh
  // counter id on every add and move it with the rule on reorder/delete
  // so React's diffing stays stable even after moves (GPT-5.5 warning #7).
  const nextKeyRef = useRef(0);
  const [uiKeys, setUiKeys] = useState<number[]>(() => value.map(() => nextKeyRef.current++));

  // Re-sync keys when `value` length drifts from what we tracked (e.g.
  // parent reloads rules after save). Worst case this regenerates keys,
  // which is still better than using array index as the key.
  useEffect(() => {
    if (uiKeys.length !== value.length) {
      setUiKeys(value.map(() => nextKeyRef.current++));
    }
  }, [value, uiKeys.length]);

  const rows: RuleRow[] = useMemo(
    () => value.map((r, i) => ({ ...r, key: uiKeys[i] ?? i })),
    [value, uiKeys]
  );

  const errors = useMemo(() => (editing ? validateDraft(draft) : {}), [editing, draft]);

  const openCreate = () => {
    setDraft(emptyDraft());
    setEditing({ index: null });
  };

  const openEdit = (row: RuleRow, index: number) => {
    // Edit flow is always single-value — multi-value is add-only.
    setDraft({ match: row.match, value: row.value, values: [], type: row.type });
    setEditing({ index });
  };

  const closeDialog = () => {
    setEditing(null);
    setDraft(emptyDraft());
  };

  const handleSave = () => {
    if (!editing || !isDraftValid(draft)) return;
    if (editing.index === null) {
      const before = value.length;
      const next = addRulesFromDraft(value, draft);
      const added = next.length - before;
      onChange(next);
      setUiKeys((prev) => {
        const fresh: number[] = [];
        for (let i = 0; i < added; i += 1) fresh.push(nextKeyRef.current++);
        return [...prev, ...fresh];
      });
    } else {
      onChange(updateRule(value, editing.index, draft));
      // keys unchanged — same position, same id.
    }
    closeDialog();
  };

  const handleDelete = (uiKey: number) => {
    const index = uiKeys.indexOf(uiKey);
    if (index < 0) return;
    onChange(deleteRule(value, index));
    setUiKeys((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMove = (uiKey: number, direction: -1 | 1) => {
    const from = uiKeys.indexOf(uiKey);
    if (from < 0) return;
    const to = from + direction;
    if (to < 0 || to >= uiKeys.length) return;
    onChange(moveRule(value, from, to));
    setUiKeys((prev) => {
      const next = prev.slice();
      const [id] = next.splice(from, 1);
      next.splice(to, 0, id);
      return next;
    });
  };

  const columns: ColumnsType<RuleRow> = [
    {
      title: 'Order',
      key: 'order',
      width: 80,
      render: (_: unknown, _row: RuleRow, index: number) => (
        <Typography.Text>{index + 1}</Typography.Text>
      ),
    },
    {
      title: 'Match',
      dataIndex: 'match',
      width: 120,
      render: (m: RoleMatchType) => (
        <Tag color={m === 'regex' ? 'geekblue' : 'default'}>
          {m === 'regex' ? 'Regex' : 'Exact'}
        </Tag>
      ),
    },
    {
      title: 'Role claim value',
      dataIndex: 'value',
      render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
    },
    {
      title: 'Maps to',
      dataIndex: 'type',
      width: 180,
      render: (t: string) => <Tag color="blue">{userTypeLabel(t)}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: unknown, row: RuleRow, index: number) => (
        <Space size="small">
          <Tooltip title="Move up (first match wins)">
            <Button
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={disabled || index === 0}
              onClick={() => handleMove(row.key, -1)}
              aria-label={`Move rule ${index + 1} up`}
            />
          </Tooltip>
          <Tooltip title="Move down">
            <Button
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={disabled || index === rows.length - 1}
              onClick={() => handleMove(row.key, 1)}
              aria-label={`Move rule ${index + 1} down`}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              disabled={disabled}
              onClick={() => openEdit(row, index)}
              aria-label={`Edit rule ${index + 1}`}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this role-mapping rule?"
            description="This removes the rule from the ordered list. The change only persists after you save the SSO configuration."
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => handleDelete(row.key)}
            disabled={disabled}
          >
            <Tooltip title="Delete">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={disabled}
                aria-label={`Delete rule ${index + 1}`}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space
        style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}
        align="center"
      >
        <Typography.Text type="secondary">
          Rules are evaluated top-to-bottom; the <strong>first match wins</strong>.
          Use the up/down arrows to change priority.
        </Typography.Text>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
          disabled={disabled}
        >
          Add Rule
        </Button>
      </Space>

      {rows.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="No role-mapping rules configured yet."
          description="Without rules, every SSO user falls back to the default user type (set in the next section)."
        />
      ) : (
        <Table<RuleRow>
          size="small"
          rowKey="key"
          columns={columns}
          dataSource={rows}
          pagination={false}
        />
      )}

      <Modal
        title={editing?.index === null ? 'Add role-mapping rule' : 'Edit role-mapping rule'}
        open={!!editing}
        onCancel={closeDialog}
        onOk={handleSave}
        okText="Save"
        okButtonProps={{ disabled: !isDraftValid(draft) }}
        destroyOnHidden
      >
        <Form layout="vertical">
          <Form.Item label="Match type" required>
            <Select
              value={draft.match}
              onChange={(match) => setDraft((d) => ({ ...d, match }))}
              options={MATCH_OPTIONS}
              aria-label="Match type"
            />
          </Form.Item>
          <Form.Item
            label={draft.match === 'regex' ? 'Regex pattern' : 'Role claim value'}
            required
            validateStatus={errors.value ? 'error' : undefined}
            help={
              errors.value ??
              (draft.match === 'regex'
                ? 'JavaScript regex syntax. Anchors optional (backend tests with .test()).'
                : editing?.index === null
                ? 'Enter one or more exact values. Press Enter or comma to add each one.'
                : 'Exact string match against the role claim from the IdP.')
            }
          >
            {editing?.index === null && draft.match === 'exact' ? (
              <Select
                mode="tags"
                tokenSeparators={[',']}
                value={draft.values}
                onChange={(vals) => setDraft((d) => ({ ...d, values: vals }))}
                placeholder="e.g. acme-admin, acme-superuser"
                style={{ width: '100%' }}
                aria-label="Rule values"
              />
            ) : (
              <Input
                value={draft.value}
                onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
                placeholder={draft.match === 'regex' ? '^acme-.*-admin$' : 'acme-admin'}
                aria-label="Rule value"
              />
            )}
          </Form.Item>
          <Form.Item
            label="Maps to user type"
            required
            validateStatus={errors.type ? 'error' : undefined}
            help={errors.type}
          >
            <Select
              value={draft.type}
              onChange={(type) => setDraft((d) => ({ ...d, type }))}
              options={USER_TYPE_OPTIONS}
              aria-label="Target user type"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
